import { useMemo } from 'react';
import {
  resolveDisplayLanguage,
  resolveProductName,
  getProductTranslationStatus,
  type Product,
  type ProductNames,
  type LanguageCode,
  type User,
  type Household,
} from '@pantry/shared';

/**
 * Core hook to get the resolved display language for a given user/household.
 * Priority: user language > household language > country default > 'en'
 *
 * @param user - The current user
 * @param household - The current household
 * @returns The resolved language code
 */
export function useDisplayLanguageCore(
  user: User | undefined,
  household: Household | null
): LanguageCode {
  return useMemo(() => {
    if (!user || !household) {
      return 'en';
    }
    return resolveDisplayLanguage(
      { language: user.language },
      { language: household.language, country: household.country }
    );
  }, [user, household]);
}

/**
 * Core hook to resolve product names using a given language.
 * Returns a function that takes a product and returns the best available name.
 *
 * @param language - The language to use for resolution
 * @returns A function that resolves product names
 */
export function useResolveProductNameCore(language: LanguageCode) {
  return useMemo(() => {
    return (
      product: Product | { name: string; names?: ProductNames | null } | null | undefined
    ) => {
      if (!product) return '';
      return resolveProductName(product as Product, language);
    };
  }, [language]);
}

/**
 * Core hook to get the resolved product name for a specific product.
 *
 * @param product - The product to get the name for
 * @param language - The language to use for resolution
 * @returns The product name in the specified language
 */
export function useProductNameCore(
  product: Product | { name: string; names?: ProductNames | null } | null | undefined,
  language: LanguageCode
): string {
  return useMemo(() => {
    if (!product) return '';
    return resolveProductName(product as Product, language);
  }, [product, language]);
}

/**
 * Core hook to get the translation status for a product.
 * Useful for showing translation contribution prompts.
 *
 * @param product - The product to check
 * @param language - The language to check against
 * @returns Translation status with canContribute, hasPreferredTranslation, etc.
 */
export function useProductTranslationStatusCore(
  product: Product | null | undefined,
  language: LanguageCode
) {
  return useMemo(() => {
    if (!product) {
      return {
        hasPreferredTranslation: false,
        hasUserContribution: false,
        fallbackLanguage: null,
        canContribute: false,
        preferredLanguage: language,
      };
    }
    const status = getProductTranslationStatus(product, language);
    return {
      ...status,
      preferredLanguage: language,
    };
  }, [product, language]);
}
